import type { CoachDetailCoach } from "#/components/shared/coach-detail-sheet";

import type { Coach } from "./booking-types";

// Adapter: map the booking-flow `Coach` shape into the
// `CoachDetailCoach` shape that the storefront's `CoachDetailSheet`
// expects. Kept as a pure function so it can be unit-tested without
// React, and so the booking-flow component stays focused on state.
//
// The two shapes overlap heavily but differ in two places:
//   - Services: booking-flow stores `serviceIds[]` + a sidecar
//     `servicePrices: { [serviceId]: priceCents }`. The sheet wants
//     `instructorServices: [{ serviceId, priceInCents }]`. We rebuild
//     that list from the price map; entries with no matching price
//     fall back to 0, which `coachServicesForSheet` will then ignore
//     because the join with the org's service list controls the final
//     rendered row.
//   - Locations: booking-flow already widens this in PR #54-follow
//     (via /api/booking/filters); we just pass through.
//
// Optional profile fields are normalized to null/[] so the sheet
// renders consistent fallback states for half-filled coaches.

export function bookingCoachToSheetCoach(coach: Coach): CoachDetailCoach {
  const instructorServices = coach.serviceIds.map((serviceId) => ({
    serviceId,
    priceInCents: coach.servicePrices?.[serviceId] ?? 0,
  }));

  return {
    id: coach.id,
    name: coach.name,
    image: coach.image,
    slug: coach.slug,
    bio: coach.bio,
    headline: coach.headline ?? null,
    extendedBio: coach.extendedBio ?? null,
    pronouns: coach.pronouns ?? null,
    specialties: coach.specialties ?? [],
    instagramUrl: coach.instagramUrl ?? null,
    tiktokUrl: coach.tiktokUrl ?? null,
    facebookUrl: coach.facebookUrl ?? null,
    instructorServices,
    locations: coach.locations ?? [],
  };
}
